import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { driverApi, escortApi, absoluteUrl } from '../api/mockApi';
import Avatar from '../components/Avatar';

const PEOPLE_TYPES = [
  { key: 'all', label: 'Drivers & PAs' },
  { key: 'driver', label: 'Drivers only' },
  { key: 'escort', label: 'PAs only' },
];

const PHOTO_FILTERS = [
  { key: 'all', label: 'Everyone' },
  { key: 'yes', label: 'Selfie uploaded' },
  { key: 'no', label: 'Awaiting selfie' },
];

const VerificationPage = () => {
  const [drivers, setDrivers] = useState([]);
  const [escorts, setEscorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [type, setType] = useState('all');
  const [photo, setPhoto] = useState('all');
  const [preview, setPreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(name), 350);
    return () => clearTimeout(t);
  }, [name]);

  const load = useCallback(async () => {
    setLoading(true);
    const [d, e] = await Promise.all([
      driverApi.getAll({ name: debouncedName }),
      escortApi.getAll({ name: debouncedName }),
    ]);
    setDrivers(d && d.success && Array.isArray(d.data) ? d.data : []);
    setEscorts(e && e.success && Array.isArray(e.data) ? e.data : []);
    setLoading(false);
  }, [debouncedName]);

  useEffect(() => { load(); }, [load]);

  const people = [
    ...(type === 'escort' ? [] : drivers.map(d => ({ ...d, _kind: 'driver' }))),
    ...(type === 'driver' ? [] : escorts.map(e => ({ ...e, _kind: 'escort' }))),
  ].filter(p => {
    if (photo === 'yes') return Boolean(p.photoUrl);
    if (photo === 'no') return !p.photoUrl;
    return true;
  });

  const withPhoto = people.filter(p => p.photoUrl).length;
  const pending = people.length - withPhoto;

  const clear = () => { setName(''); setDebouncedName(''); setType('all'); setPhoto('all'); };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Selfie Verification</h1>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{people.length}</div>
          <div className="stat-label">People shown</div>
        </div>
        <div className="stat-card on-route">
          <div className="stat-value">{withPhoto}</div>
          <div className="stat-label">Selfie uploaded</div>
        </div>
        <div className="stat-card off-route">
          <div className="stat-value">{pending}</div>
          <div className="stat-label">Awaiting selfie</div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="filter-field">
          <label>Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Search name…" />
        </div>
        <div className="filter-field">
          <label>Show</label>
          <select value={type} onChange={e => setType(e.target.value)}>
            {PEOPLE_TYPES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label>Selfie</label>
          <select value={photo} onChange={e => setPhoto(e.target.value)}>
            {PHOTO_FILTERS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div className="filters-actions">
          <button className="btn btn-outline btn-sm" onClick={clear}>Clear filters</button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : people.length === 0 ? (
        <p className="empty-state">Nobody matches these filters.</p>
      ) : (
        <div className="verify-grid">
          {people.map(p => (
            <div className="verify-card" key={`${p._kind}-${p.id}`}>
              <Avatar
                name={p.fullName}
                photoUrl={p.photoUrl}
                size={104}
                onClick={p.photoUrl ? () => setPreview(p) : undefined}
              />
              <div className="verify-name">{p.fullName || 'Unnamed'}</div>
              <div className="verify-sub">{p.mobileNumber || '—'}</div>
              <div className="verify-sub">{p.area || 'No area'}</div>
              <span className={`pill ${p._kind === 'driver' ? '' : 'pill-pa'}`}>
                {p._kind === 'driver' ? 'Driver' : 'PA'}
              </span>
              <div style={{ marginTop: 10 }}>
                {p.photoUrl ? (
                  <span className="distance-badge within">Selfie on file</span>
                ) : (
                  <span className="distance-badge outside">Awaiting selfie</span>
                )}
              </div>
              <button
                className="btn btn-outline btn-sm"
                style={{ marginTop: 12 }}
                onClick={() => navigate(p._kind === 'driver' ? `/drivers/${p.id}` : `/escorts/${p.id}`)}
              >
                View profile
              </button>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
            <h3>{preview.fullName || 'Verification selfie'}</h3>
            <img
              src={absoluteUrl(preview.photoUrl)}
              alt={`${preview.fullName || 'Person'} verification selfie`}
              style={{ maxWidth: '100%', maxHeight: '60vh', borderRadius: 12, border: '1px solid var(--border-gold)' }}
            />
            <div className="modal-actions" style={{ justifyContent: 'center' }}>
              <button className="btn btn-outline" onClick={() => setPreview(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationPage;
